import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type FormEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import type { Category, Check, CheckCreate, CheckSummary, Favorite, Product } from "../api/types";
import { Card, HangTag, vibrate } from "../components/Bits";
import { looksLikeLink, shortTitle, sortedSizes } from "../lib/fit";
import { onboardingSkipped, useApp, type BoardDraft, type RefChoice } from "../state";
import { Steps } from "../components/Steps";
import { rowsFor } from "../lib/piece";
import { AddSize } from "./AddSize";
import { ScreenshotSizes } from "./ScreenshotSizes";
import { DescribeForm } from "./DescribePiece";
import { MeasureBox, ReadingBox } from "./MeasureBox";
import { Results } from "./Results";

const CAT_CHIPS: [Category, string][] = [
  ["pants", "Pants"],
  ["top", "Top"],
  ["dress", "Dress"],
  ["skirt", "Skirt"],
];
const SAMPLES: { cat: Category; label: string; cand: string; ref: string; refName: string }[] = [
  { cat: "pants", label: "Wide-leg jeans", cand: "https://www.madewell.com/the-perfect-vintage-wide-leg-jean-NM458.html", ref: "https://www.levi.com/US/en_US/clothing/women/jeans/501-original-fit-womens-jeans/p/125010000", refName: "Levi's 501s" },
  { cat: "top", label: "V-neck tee", cand: "https://www.everlane.com/products/womens-organic-cotton-v-neck-tee-white", ref: "https://www.uniqlo.com/us/en/products/E455759-000/airism-cotton-oversized-crew-neck-t-shirt", refName: "oversized tee" },
  { cat: "dress", label: "Linen midi dress", cand: "https://www.thereformation.com/products/juliette-linen-midi-dress/1311987.html", ref: "https://www.jcrew.com/p/womens/categories/clothing/dresses/smocked-shift-dress/BW123", refName: "smocked dress" },
];

const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms));
const isWaiting = (p: Product | null | undefined) => p?.status === "extracting";

function refProduct(ref: RefChoice | null): Product | null {
  if (!ref) return null;
  return ref.kind === "fav" ? ref.fav.product : ref.product;
}
function refSize(ref: RefChoice | null): string | null {
  if (!ref) return null;
  return ref.kind === "fav" ? ref.fav.size_label : ref.size;
}
function refVariant(ref: RefChoice | null): string | null {
  if (!ref) return null;
  return ref.kind === "fav" ? ref.fav.variant : ref.variant;
}
/** The fit a listing itself is (a Petite page is Petite), so the fit picker starts there, not on its first option. */
function listingFit(p: Product): string | null {
  const line = p.style?.line;
  return (line && p.variants?.find((v) => v.toLowerCase().includes(line))) || null;
}

function sizeTag(size: string | null, variant: string | null): string | null {
  if (!size) return null;
  return size + (variant && !/regular/i.test(variant) ? " " + variant : "");
}
function refSig(ref: RefChoice | null): string {
  if (!ref) return "";
  if (ref.kind === "fav") return "fav:" + ref.fav.id;
  return `new:${ref.product.id}:${ref.size}:${ref.variant ?? ""}:${ref.wouldChange.slice().sort().join(",")}`;
}
function checkRefSig(c: Check): string {
  if (c.reference.favorite_id) return "fav:" + c.reference.favorite_id;
  return `new:${c.reference.product.id}:${c.reference.size_label}:${c.reference.variant ?? ""}:`;
}

export function draftFromCheck(c: Check): BoardDraft {
  const ref: RefChoice = c.reference.favorite_id
    ? { kind: "fav", fav: { id: c.reference.favorite_id, product: c.reference.product, size_label: c.reference.size_label, variant: c.reference.variant, would_change: [] } }
    : { kind: "new", product: c.reference.product, size: c.reference.size_label, variant: c.reference.variant, wouldChange: [], saveAsFavorite: false };
  return { candRaw: c.candidate.url, cand: c.candidate, tappedCat: c.candidate.category ? null : c.category, refRaw: c.reference.product.url, ref, check: c };
}

/** Polls GET /api/products/{id} once a second while the product is still being read. */
function useProductPoll(p: Product | null, onUpdate: (p: Product) => void) {
  const cb = useRef(onUpdate);
  cb.current = onUpdate;
  useEffect(() => {
    if (!p || p.status !== "extracting") return;
    let alive = true;
    const id = window.setTimeout(() => {
      api.product(p.id).then(
        ({ product }) => alive && cb.current(product),
        () => alive && cb.current({ ...p }), // transient error: try again next tick
      );
    }, 1000);
    return () => {
      alive = false;
      window.clearTimeout(id);
    };
  }, [p]);
}

export function Board() {
  const { id: routeId } = useParams();
  const { me, meta, draft: d, setDraft, fav } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const justSaved = (loc.state as { savedFavorite?: string } | null)?.savedFavorite ?? null;
  const [favs, setFavs] = useState<Favorite[] | null>(null);
  const [pending, setPending] = useState<CheckSummary | null>(null);
  const [errs, setErrs] = useState<{ cand?: string; ref?: string; check?: string; load?: string }>({});
  const [busy, setBusy] = useState<{ cand?: boolean; ref?: boolean }>({});
  const [measuring, setMeasuring] = useState(false);
  const [fx, setFx] = useState<{ tagA?: boolean; tagB?: boolean; results?: boolean }>({});
  const [showSizes, setShowSizes] = useState(false);
  // The piece that fits well: describing it comes first, since people often can't find its link.
  const [refMode, setRefMode] = useState<"describe" | "link">("describe");
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadSide = useCallback(() => {
    api.favorites().then(
      (x) => alive.current && setFavs(x.favorites),
      () => alive.current && setFavs([]),
    );
    api.checks().then(
      (x) => alive.current && setPending(x.checks.find((c) => c.status === "bought") ?? x.checks.find((c) => c.status === "new") ?? null),
      () => {},
    );
  }, []);
  useEffect(loadSide, [loadSide]);

  // Opening /check/:id loads that check into the board.
  useEffect(() => {
    if (!routeId || d.check?.id === routeId) return;
    setErrs({});
    api.check(routeId).then(
      ({ check }) => {
        if (!alive.current) return;
        setDraft(() => draftFromCheck(check));
        setFx({ results: true });
      },
      (e) => alive.current && setErrs({ load: errorMessage(e) }),
    );
  }, [routeId, d.check?.id, setDraft]);

  useProductPoll(d.cand, (p) => setDraft((x) => (x.cand?.id === p.id ? { ...x, cand: p } : x)));
  const rp = refProduct(d.ref);
  useProductPoll(d.ref?.kind === "new" ? rp : null, (p) =>
    setDraft((x) => (x.ref?.kind === "new" && x.ref.product.id === p.id ? { ...x, ref: { ...x.ref, product: p } } : x)),
  );

  const cat: Category | null = d.cand?.category ?? d.tappedCat;
  const catMeta = cat ? meta.categories[cat] : null;
  const favList = useMemo(() => favs ?? [], [favs]);
  // The favorite to show and reuse: the one just saved, else the newest (the list comes newest first).
  const myFav = favList.find((f) => f.id === (justSaved ?? fav.savedId)) ?? favList[0] ?? null;

  // New shoppers start with the piece they love; once they've saved one, or chosen to skip, the board is home.
  useEffect(() => {
    if (routeId || favs === null || favs.length || d.cand || onboardingSkipped()) return;
    nav("/favorite", { replace: true });
  }, [routeId, favs, d.cand, nav]);

  // With a saved favorite of the same kind, it's the yardstick unless they pick another.
  const autoPicked = useRef<string | null>(null);
  useEffect(() => {
    if (!cat || d.ref || !d.cand || autoPicked.current === d.cand.id) return;
    const same = favList.filter((f) => (f.product.category ?? cat) === cat);
    const pick = same.find((f) => f.id === myFav?.id) ?? same[0];
    autoPicked.current = d.cand.id;
    if (pick) setDraft((x) => ({ ...x, ref: { kind: "fav", fav: pick }, refRaw: pick.product.url }));
  }, [cat, d.ref, d.cand, favList, myFav, setDraft]);

  /* ---------- actions ---------- */
  const addCand = async (raw: string) => {
    setDraft((x) => ({ ...x, candRaw: raw }));
    const url = looksLikeLink(raw);
    if (!url) {
      setErrs({ cand: raw.trim() ? "That doesn't look like a link. Copy it from the address bar or the share button." : "Paste a link first." });
      return;
    }
    setErrs({});
    setBusy({ cand: true });
    try {
      const { product } = await api.resolve(url);
      setDraft((x) => ({ ...x, cand: product, tappedCat: null, ref: null, refRaw: "", check: null }));
      setFx({ tagA: true });
      vibrate();
    } catch (e) {
      setErrs({ cand: errorMessage(e) });
    } finally {
      setBusy({});
    }
  };
  const addRef = async (raw: string) => {
    setDraft((x) => ({ ...x, refRaw: raw }));
    const url = looksLikeLink(raw);
    if (!url) {
      setErrs({ ref: raw.trim() ? "That doesn't look like a link. Copy it from the address bar or the share button." : "Paste a link first." });
      return;
    }
    setErrs({});
    setBusy({ ref: true });
    try {
      const { product } = await api.resolve(url);
      setDraft((x) => ({ ...x, ref: { kind: "new", product, size: null, variant: listingFit(product), wouldChange: [], saveAsFavorite: true } }));
      setFx({ tagB: true });
      setShowSizes(false);
    } catch (e) {
      setErrs({ ref: errorMessage(e) });
    } finally {
      setBusy({});
    }
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>, which: "cand" | "ref") => {
    const el = e.currentTarget;
    window.setTimeout(() => {
      if (looksLikeLink(el.value) && /\./.test(el.value)) void (which === "cand" ? addCand : addRef)(el.value);
    }, 0);
  };
  const updateRef = (f: (r: Extract<RefChoice, { kind: "new" }>) => Partial<Extract<RefChoice, { kind: "new" }>>) =>
    setDraft((x) => (x.ref?.kind === "new" ? { ...x, ref: { ...x.ref, ...f(x.ref) } } : x));

  const clearCand = () => {
    setDraft(() => ({ candRaw: "", cand: null, tappedCat: null, refRaw: "", ref: null, check: null }));
    setShowSizes(false);
    if (routeId) nav("/");
  };
  const clearRef = () => {
    setDraft((x) => ({ ...x, ref: null, refRaw: "" }));
    setShowSizes(false);
    window.setTimeout(() => document.getElementById("ref-link")?.focus({ preventScroll: true }), 30);
  };
  const pickFav = (f: Favorite) => {
    setDraft((x) => ({ ...x, ref: { kind: "fav", fav: f }, refRaw: f.product.url }));
    setFx({ tagB: true });
    setShowSizes(false);
    vibrate();
  };
  const changeSize = () => {
    if (d.ref?.kind === "fav") {
      const f = d.ref.fav;
      setDraft((x) => ({ ...x, ref: { kind: "new", product: f.product, size: f.size_label, variant: f.variant, wouldChange: f.would_change, saveAsFavorite: true } }));
    }
    setShowSizes(true);
  };

  const measure = async () => {
    if (!d.cand || !cat || !d.ref) return;
    const ref = d.ref;
    const body: CheckCreate = {
      candidate_product_id: d.cand.id,
      ...(d.cand.category ? {} : { category: cat }),
      reference:
        ref.kind === "fav"
          ? { favorite_id: ref.fav.id }
          : {
              product_id: ref.product.id,
              size_label: ref.size!,
              ...(ref.variant ? { variant: ref.variant } : {}),
              ...(ref.wouldChange.length ? { would_change: ref.wouldChange } : {}),
              ...(ref.fitWish ? { fit_wish: ref.fitWish } : {}),
              save_as_favorite: ref.saveAsFavorite,
            },
    };
    setErrs({});
    setMeasuring(true);
    setShowSizes(false);
    window.setTimeout(() => document.getElementById("measure")?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
    const t0 = Date.now();
    try {
      let { check } = await api.createCheck(body);
      // status "waiting" = a product is still being read; poll until it settles.
      while (check.status === "waiting" && alive.current) {
        await sleep(1000);
        check = (await api.check(check.id)).check;
      }
      await sleep(Math.max(0, 2300 - (Date.now() - t0)));
      if (!alive.current) return;
      setDraft((x) => ({
        ...x,
        check,
        ref:
          x.ref?.kind === "new" && check.reference.favorite_id
            ? { kind: "fav", fav: { id: check.reference.favorite_id, product: check.reference.product, size_label: check.reference.size_label, variant: check.reference.variant, would_change: x.ref.wouldChange } }
            : x.ref,
      }));
      setFx({ results: true });
      setMeasuring(false);
      loadSide();
      window.setTimeout(() => document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
    } catch (e) {
      if (!alive.current) return;
      setErrs({ check: errorMessage(e) });
      setMeasuring(false);
    }
  };

  /* ---------- derived ---------- */
  const c = d.check;
  const differs = !!c && (!d.cand || c.candidate.id !== d.cand.id || refSig(d.ref) !== checkRefSig(c));
  const settled = !!c && !differs && !showSizes && !measuring;
  const rSize = refSize(d.ref);
  const rVar = refVariant(d.ref);
  const refDone = !!rp && !!rSize;
  const candBlocked = !d.cand || d.cand.status === "failed";
  const refBlocked = !rp || rp.status === "failed" || !rSize;
  const ready = !!cat && !candBlocked && !refBlocked;
  const name = me.user.name?.split(" ")[0];
  const nudge = pending && pending.id !== c?.id ? pending : null;

  if (errs.load && routeId && !c) {
    return (
      <section className="view enter">
        <div className="banner bad">
          <b>We couldn't open those notes.</b>
          <p className="small">{errs.load}</p>
        </div>
        <button className="btn" type="button" onClick={() => nav("/history")}>
          See your history
        </button>
      </section>
    );
  }
  if (!routeId && favs === null) {
    return (
      <section className="view">
        <p className="eyebrow">Loading…</p>
      </section>
    );
  }
  if (routeId && !c) {
    return (
      <section className="view">
        <p className="eyebrow">Loading your notes…</p>
      </section>
    );
  }

  return (
    <section className="view enter">
      {!routeId && (
        <Steps
          at={2}
          favDone={favList.length > 0}
          eyeingDone={!!d.cand}
          answered={(favList.length ? rowsFor(fav.cat).length : 0) + (d.cand ? 1 : 0)}
          questions={rowsFor(fav.cat).length}
        />
      )}
      {!routeId && !d.cand && myFav && (
        <div className="saved">
          <div className="savedh">
            <span className={"eyebrow" + (justSaved ? " ok" : "")}>{justSaved ? "Saved to your profile" : "Your favorite"}</span>
            <button className="linkish" type="button" onClick={() => nav("/favorite")}>
              {fav.savedId === myFav.id ? "Edit" : "Add another"}
            </button>
          </div>
          <HangTag p={myFav.product} size={sizeTag(myFav.size_label, myFav.variant)} />
        </div>
      )}
      {!routeId && !d.cand && !myFav && (
        <div className="nofav">
          <span>Your favorite isn't added yet.</span>
          <button className="linkish" type="button" onClick={() => nav("/favorite")}>
            Add it
          </button>
        </div>
      )}
      {myFav && !d.cand && !routeId ? (
        <h1>
          <span className="ph">Now, what are you</span> <span className="ph">
            <em>eyeing?</em>
          </span>
        </h1>
      ) : (
        <h1>
          Hi{name ? " " + name : " there"}. <span className="ph">What are you</span>{" "}
          <span className="ph">
            <em>eyeing</em> today?
          </span>
        </h1>
      )}

      {/* Card 1 */}
      <Card tag="The one you're eyeing" mark={cat ? "✓" : "1"} done={!!cat}>
        {d.cand ? (
          <>
            <HangTag p={d.cand} cat={cat} swing={fx.tagA} />
            {isWaiting(d.cand) && <ReadingBox p={d.cand} />}
            {d.cand.status === "failed" && (
              <p className="hint warn" role="alert">
                {d.cand.error || "We couldn't read that page."}
              </p>
            )}
            {d.cand.status === "needs_review" && <p className="hint">We're double-checking this page, so the notes may be a little less sure.</p>}
            {!isWaiting(d.cand) && (d.cand.size_source === "standard" || d.cand.size_source === "brand_usual" || d.cand.size_source === "shopper") && (
              <p className="small muted">{d.cand.size_line}</p>
            )}
            {!isWaiting(d.cand) && (d.cand.size_source === "standard" || d.cand.size_source === "brand_usual") && (
              <ScreenshotSizes product={d.cand} onConfirmed={(p) => setDraft((x) => (x.cand?.id === p.id ? { ...x, cand: p } : x))} />
            )}
            {!d.cand.category && d.cand.status !== "failed" && (
              <>
                <p className="aside">
                  We couldn't tell what this is. <b>Tap one:</b>
                </p>
                <div className="row" role="group" aria-label="What kind of piece is it?">
                  {CAT_CHIPS.map(([k, label]) => (
                    <button
                      key={k}
                      className="chip"
                      type="button"
                      aria-pressed={d.tappedCat === k}
                      onClick={() => {
                        vibrate();
                        setDraft((x) => ({ ...x, tappedCat: k, ref: x.tappedCat === k ? x.ref : null }));
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
            <button className="linkish" type="button" onClick={clearCand}>
              Paste a different link
            </button>
          </>
        ) : (
          <>
            <form
              className="stack"
              noValidate
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                void addCand(d.candRaw);
              }}
            >
              <div className="seam">
                <input
                  id="cand-link"
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  placeholder="Paste a product link"
                  aria-label="Link to the item you're considering"
                  value={d.candRaw}
                  onChange={(e) => setDraft((x) => ({ ...x, candRaw: e.target.value }))}
                  onPaste={(e) => onPaste(e, "cand")}
                />
                <button className="btn primary" type="submit" disabled={busy.cand}>
                  Add
                </button>
              </div>
              {errs.cand && <p className="hint warn">{errs.cand}</p>}
            </form>
            <p className="aside">
              Any store. No link handy? <b>Try one:</b>
            </p>
            <div className="row">
              {SAMPLES.map((s) => (
                <button key={s.cat} className="chip sm lite" type="button" onClick={() => void addCand(s.cand)} disabled={busy.cand}>
                  {s.label}
                </button>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Card 2 */}
      <Card tag="One that fits you really well" mark={refDone ? "✓" : "2"} done={refDone} dim={!cat}>
        {!cat || !catMeta ? (
          <p className="aside">
            Your best-fitting piece of the same kind is the yardstick. <mark>Any brand works.</mark>
          </p>
        ) : d.ref && rp ? (
          <>
            <HangTag p={rp} cat={cat} swing={fx.tagB} size={sizeTag(rSize, rVar)} />
            {rp.category && rp.category !== cat && (
              <p className="hint warn">
                This looks like a {meta.categories[rp.category]?.one ?? rp.category}. The comparison works best with {catMeta.name}.
              </p>
            )}
            {isWaiting(rp) && <ReadingBox p={rp} />}
            {rp.status === "failed" && <p className="hint warn">{rp.error || "We couldn't read that page."}</p>}
            {d.ref.kind === "fav" || (settled && !showSizes) ? (
              <div className="row" style={{ gap: 18 }}>
                <button className="linkish" type="button" onClick={changeSize}>
                  Change my size
                </button>
                <button className="linkish" type="button" onClick={clearRef}>
                  Use a different piece
                </button>
              </div>
            ) : (
              rp.status !== "failed" && (
                <div className="stack">
                  {rp.sizes.length > 0 && <p className="aside">What size is yours?</p>}
                  {rp.variants && rp.variants.length > 1 && (
                    <div className="row" role="group" aria-label="Fit">
                      <span className="eyebrow" style={{ marginRight: 4 }}>
                        Fit
                      </span>
                      {rp.variants.map((v) => (
                        <button key={v} className="chip sm" type="button" aria-pressed={(rVar ?? listingFit(rp)) === v} onClick={() => updateRef(() => ({ variant: v }))}>
                          {v}
                        </button>
                      ))}
                    </div>
                  )}
                  {rp.sizes.length > 0 && (
                    <div className="row" role="group" aria-label="Your size">
                      {sortedSizes(rp.sizes).map((s) => (
                        <button
                          key={s}
                          className={"chip size" + (rSize === s ? " just" : "")}
                          type="button"
                          aria-pressed={rSize === s}
                          onClick={() => {
                            vibrate();
                            setShowSizes(false);
                            updateRef(() => ({ size: s }));
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                  {rp.size_line && <p className="small muted">{rp.size_line}</p>}
                  {d.ref.kind === "new" && rp.status !== "extracting" && (rp.size_source === "standard" || rp.size_source === "brand_usual") && (
                    <ScreenshotSizes
                      product={rp}
                      onConfirmed={(p) =>
                        setDraft((x) => {
                          const r = x.ref;
                          if (r?.kind !== "new" || r.product.id !== p.id) return x;
                          // Keep the size they picked only if the listing really has it.
                          return { ...x, ref: { ...r, product: p, size: p.sizes.some((s) => s.label === r.size) ? r.size : null } };
                        })
                      }
                    />
                  )}
                  {d.ref.kind === "new" && rp.sizes.length > 0 && rp.status !== "extracting" && rp.size_source !== "described" && (
                    <AddSize
                      product={rp}
                      onAdded={(p, size, fit) =>
                        setDraft((x) =>
                          x.ref?.kind === "new" && x.ref.product.id === p.id
                            ? { ...x, ref: { ...x.ref, product: p, size: size ?? x.ref.size, variant: fit ?? x.ref.variant } }
                            : x,
                        )
                      }
                    />
                  )}
                  {rSize && catMeta.would_change.length > 0 && d.ref.kind === "new" && (
                    <>
                      <p className="aside">
                        Anything you'd change about it? <span className="muted small">Optional</span>
                      </p>
                      <div className="row">
                        {catMeta.would_change.map((w) => {
                          const on = d.ref?.kind === "new" && d.ref.wouldChange.includes(w.id);
                          return (
                            <button
                              key={w.id}
                              className="chip sm lite"
                              type="button"
                              aria-pressed={on}
                              onClick={() => updateRef((r) => ({ wouldChange: on ? r.wouldChange.filter((x) => x !== w.id) : [...r.wouldChange, w.id] }))}
                            >
                              {w.label}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                  <div className="row" style={{ gap: 18 }}>
                    <button className="linkish" type="button" onClick={clearRef}>
                      Use a different piece
                    </button>
                  </div>
                </div>
              )
            )}
          </>
        ) : (
          <>
            {favList.filter((f) => (f.product.category ?? cat) === cat).length > 0 && (
              <>
                <p className="aside">One tap to reuse a piece you've told us about:</p>
                <div className="stack">
                  {favList
                    .filter((f) => (f.product.category ?? cat) === cat)
                    .map((f) => (
                      <button key={f.id} className="btn" type="button" style={{ justifyContent: "space-between" }} onClick={() => pickFav(f)}>
                        <span>
                          {f.product.brand} {shortTitle(f.product.title)}
                        </span>
                        <span className="num">{sizeTag(f.size_label, f.variant)}</span>
                      </button>
                    ))}
                </div>
              </>
            )}
            <div className="row" role="tablist" aria-label="How to add it">
              <button className="chip sm" type="button" role="tab" aria-selected={refMode === "describe"} onClick={() => setRefMode("describe")}>
                Describe it
              </button>
              <button className="chip sm" type="button" role="tab" aria-selected={refMode === "link"} onClick={() => setRefMode("link")}>
                Paste its link
              </button>
            </div>
            {refMode === "describe" ? (
              <DescribeForm
                key={cat}
                cat={cat}
                brands={meta.brands ?? []}
                tailoring={catMeta.tailoring_options}
                wouldChange={catMeta.would_change}
                onDone={({ product, size, fit, wouldChange, fitWish }) => {
                  setDraft((x) => ({
                    ...x,
                    ref: { kind: "new", product, size, variant: fit, wouldChange, ...(fitWish ? { fitWish } : {}), saveAsFavorite: true },
                  }));
                  setFx({ tagB: true });
                  setShowSizes(false);
                }}
              />
            ) : (
              <form
                className="stack"
                noValidate
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  void addRef(d.refRaw);
                }}
              >
                <div className="seam">
                  <input
                    id="ref-link"
                    type="url"
                    inputMode="url"
                    autoComplete="off"
                    placeholder={`Link to your best-fitting ${catMeta.one.split(" ").pop()}`}
                    aria-label="Link to a piece that fits you well"
                    value={d.refRaw}
                    onChange={(e) => setDraft((x) => ({ ...x, refRaw: e.target.value }))}
                    onPaste={(e) => onPaste(e, "ref")}
                  />
                  <button className="btn primary" type="submit" disabled={busy.ref}>
                    Add
                  </button>
                </div>
                {errs.ref && <p className="hint warn">{errs.ref}</p>}
              </form>
            )}
            {refMode === "link" && SAMPLES.find((s) => s.cat === cat) && (
              <p className="aside">
                Think of the one you reach for first.{" "}
                <b>
                  <button className="linkish" type="button" style={{ fontSize: "inherit" }} onClick={() => void addRef(SAMPLES.find((s) => s.cat === cat)!.ref)}>
                    Use the sample {SAMPLES.find((s) => s.cat === cat)!.refName}
                  </button>
                </b>
              </p>
            )}
          </>
        )}
      </Card>

      {measuring && d.cand && rp && (
        <MeasureBox
          cand={d.cand}
          refBrand={rp.brand}
          refTag={sizeTag(rSize, rVar) ?? ""}
          fitPoints={catMeta?.fit_points.length ?? 0}
          sizes={d.cand.sizes.length}
          done={false}
        />
      )}
      {!measuring && (!c || differs) && (
        <div className="stack">
          <button className="btn primary" type="button" disabled={!ready} onClick={() => void measure()}>
            {c ? "Update the fit notes" : "Lay them side by side"}
          </button>
          {errs.check && (
            <p className="hint warn" role="alert" style={{ textAlign: "center" }}>
              {errs.check}
            </p>
          )}
          {!ready && (
            <p className="aside" style={{ textAlign: "center" }}>
              {!d.cand ? "Start with the link above." : candBlocked ? "Try a different link above." : !cat ? "Tap what kind of piece it is." : !rp ? "Now the piece that fits you." : "Tap your size in it."}
            </p>
          )}
        </div>
      )}
      {c && !measuring && !differs && (
        <>
          <MeasureBox
            cand={c.candidate}
            refBrand={c.reference.product.brand}
            refTag={sizeTag(c.reference.size_label, c.reference.variant) ?? ""}
            fitPoints={c.result ? c.result.by_size[c.result.sizes[0]]?.fit_points.length ?? 0 : catMeta?.fit_points.length ?? 0}
            sizes={c.result?.sizes.length ?? c.candidate.sizes.length}
            personalizedFrom={c.result?.personalized_from}
            done
          />
          {c.status === "failed" || !c.result ? (
            <div className="banner bad" role="alert">
              <b>We couldn't make fit notes for this one.</b>
              <p className="small">{c.candidate.error || c.reference.product.error || "One of the pages couldn't be read. Try a different link."}</p>
            </div>
          ) : (
            <Results key={c.id} check={c} animate={!!fx.results} />
          )}
        </>
      )}

      {nudge && (
        <div className="nudge">
          <p className="small">
            What happened with the <b>{nudge.candidate.brand + " " + shortTitle(nudge.candidate.title)}</b>? Bought it or passed on it, either answer sharpens your notes.
          </p>
          <button className="chip sm" type="button" onClick={() => nav(`/check/${nudge.id}/report`)}>
            Update
          </button>
        </div>
      )}
    </section>
  );
}
